import { BookOpen, LayoutDashboard, Plug, Settings, ShoppingCart } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { catalogNavEntries } from "@/features/catalog/nav";
import { identityNavEntries } from "@/features/identity/nav";
import { ledgerNavEntries } from "@/features/ledger/nav";
import { financeAccountingNavEntries, financeNavEntries } from "@/features/finance/nav";
import { inventoryNavEntries } from "@/features/inventory/nav";
import { partnersNavEntries } from "@/features/partners/nav";
import { purchasingNavEntries } from "@/features/purchasing/nav";
import { salesNavEntries } from "@/features/sales/nav";
import { channelsNavEntries } from "@/features/channels/nav";
import type { AppCode } from "@/lib/modules";
import { appsNavEntries } from "@/features/apps/nav";
import { platformNavEntries } from "@/features/platform/nav";

/**
 * Source of truth for the sidebar/navigation. Routes themselves live in
 * src/router.tsx; this registry only describes what the shell renders.
 * Entries with `children` are groups: a group renders as a section, never
 * as a link, and disappears when every child is filtered out.
 */
export interface NavEntry {
  /** Route path for leaves; a path prefix for groups (used for matching). */
  path: string;
  /** i18n key in the "common" namespace, e.g. "nav.dashboard". */
  labelKey: string;
  icon: LucideIcon;
  /** Exact permission string required to see this entry (and its subtree). */
  permission?: string;
  /**
   * The app a GROUP belongs to. Five apps now share the `/finance` namespace,
   * so a group's path no longer identifies its owner on its own.
   */
  app?: AppCode;
  children?: NavEntry[];
}

export const NAV_ENTRIES: NavEntry[] = [
  {
    path: "/",
    labelKey: "nav.dashboard",
    icon: LayoutDashboard,
  },
  ...financeNavEntries,
  {
    path: "/ledger",
    labelKey: "nav.ledger",
    icon: BookOpen,
    // A domain with a purpose-built screen contributes its own entry; the
    // registry supplies the rest of the group.
    children: [...ledgerNavEntries, ...financeAccountingNavEntries],
  },
  ...inventoryNavEntries,
  ...purchasingNavEntries,
  {
    path: "/sales",
    labelKey: "nav.sales",
    icon: ShoppingCart,
    children: salesNavEntries,
  },
  ...catalogNavEntries,
  ...partnersNavEntries,
  {
    path: "/channels",
    labelKey: "nav.channels",
    icon: Plug,
    children: channelsNavEntries,
  },
  {
    path: "/admin",
    labelKey: "nav.settings",
    icon: Settings,
    children: [...identityNavEntries, ...platformNavEntries, ...appsNavEntries],
  },
];

/**
 * Prune the tree to what the current user may see: entries whose
 * `permission` fails the check are dropped, and groups left with no
 * visible children are dropped with them.
 */
export function filterNav(
  entries: NavEntry[],
  hasPermission: (permission: string) => boolean,
): NavEntry[] {
  const visible: NavEntry[] = [];
  for (const entry of entries) {
    if (entry.permission && !hasPermission(entry.permission)) continue;
    if (entry.children) {
      const children = filterNav(entry.children, hasPermission);
      if (children.length === 0) continue;
      visible.push({ ...entry, children });
    } else {
      visible.push(entry);
    }
  }
  return visible;
}
